import React, {useState} from 'react';
import {ArrowRight, Mail, Eye, LineChart, Folder, Loader2} from 'lucide-react';
import {Link, useNavigate} from "react-router-dom";
import {LiaUserEditSolid} from "react-icons/lia";
import Swal from "sweetalert2";
import useApi from "../../../utils/useApi";
import {ImProfile} from "react-icons/im";
import Button from "../../UI/Button";
import authApi from "../../../utils/auth.api";
import {IoCheckmarkDoneCircleOutline} from "react-icons/io5";

const ClientAccountActions = ({ user, isArchived, className, includeProgressBtn = false, includeProfileBtn = false, onArchiveSuccess, year }) => {
    const api = useApi();
    const { createThread } = useApi();
    const navigate = useNavigate();
    const [sendingPassword, setSendingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    const handleArchiveToggle = async (clientId, isArchived, clientName) => {
        try {
            const result = await Swal.fire({
                                               title: 'Are you sure?',
                                               text: `Do you want to ${isArchived ? 'unarchive' : 'archive'} client ${clientName}?`,
                                               icon: 'question',
                                               showCancelButton: true,
                                               confirmButtonColor: '#3085d6',
                                               cancelButtonColor: '#d33',
                                               confirmButtonText: isArchived ? 'Yes, unarchive!' : 'Yes, archive!',
                                               cancelButtonText: 'Cancel'
                                           });

            if (result.isConfirmed) {
                let response;
                if (isArchived) {
                    response = await api.unarchiveClient(clientId);
                } else {
                    response = await api.archiveClient(clientId);
                }

                onArchiveSuccess();

                // Show success message
                await Swal.fire({
                                    title: 'Success!',
                                    text: response.message,
                                    icon: 'success',
                                    timer: 2000,
                                    showConfirmButton: false
                                });

            }
        }
        catch (error) {
            // Show error message
            await Swal.fire({
                                title: 'Error!',
                                text: error.message,
                                icon: 'error'
                            });

        }
    };

    const sendPasswordReset = async (user) => {
        try {
            setSendingPassword(true);
            const response = await authApi.forgotPassword(user.client_id);

            if (response.success) {
                setPasswordSuccess(response.message);
                setPasswordError('');
            } else {
                setPasswordError(response.message);
                setPasswordSuccess('');
            }
        }
        catch (err) {
            setPasswordError(err.message);
            setPasswordSuccess('');
            setPasswordErrorTimer();
        }

        setSendingPassword(false);
    }

    const handleSendMessage = async (client) => {
        let data = {
            uid: client.id,
        }

        data.newMessage = true;

        const response = await createThread(data);

        navigate(`/messages/${response.thread.id}`);
    }

    const setPasswordErrorTimer = () => {
        setTimeout(() => {
            setPasswordError(false);
        }, 30000);
    }

    return (
        <>
            <div className={`grid grid-cols-1
        sm:grid-cols-1
        md:grid-cols-1
        lg:grid-cols-3
        min-[1315px]:grid-cols-3
        min-[1100px]:grid-cols-1
        max-
        gap-4 w-full ${className ? className : ''}`}>
                {/* Profile Section */}

                <div className="flex flex-row flex-wrap gap-2 flex-grow">
                    <Link
                        to={`/client/${user.id}/edit?year=${year}`}
                        className="w-full block px-4 py-2 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                        <LiaUserEditSolid size={24}/> Update {user.profile.client}
                    </Link>
                    <div className={`w-full flex flex-row justify-between gap-2 text-sm`}>
                        <Button
                            variant={passwordSuccess ? 'success' : passwordError ? 'danger' : 'secondary'}
                            onClick={() => sendPasswordReset(user)}
                            disabled={passwordSuccess}
                        >
                            {(() => {
                                const commonSpanClasses = "flex flex-row gap-2 items-center";

                                if (sendingPassword) {
                                    return (
                                        <span className={commonSpanClasses}>
                                      Sending Password Reset...
                                      <Loader2 className="w-4 h-4 animate-spin"/>
                                    </span>
                                    );
                                }

                                if (passwordSuccess) {
                                    return (
                                        <span className={commonSpanClasses}>
                                      Sent Password Reset
                                      <IoCheckmarkDoneCircleOutline/>
                                    </span>
                                    );
                                }

                                if (passwordError) {
                                    return (
                                        <span className={commonSpanClasses}>
                                      {passwordError}
                                    </span>
                                    );
                                }

                                return <span>Send Password Reset</span>;
                            })()}
                        </Button>
                        <Button onClick={(e) => handleSendMessage(user)}>
                            Send Message
                        </Button>
                    </div>
                </div>

                {/* Main Actions */}
                <div className="flex flex-row flex-wrap gap-2 flex-grow">
                    <Link
                        to={`/client/questionnaire/${user.id}?year=${year}`}
                        className="flex-grow flex justify-center px-4 py-2 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-center"
                    >
                        Tax Questionnaire <i className="fas fa-newspaper ml-2"/>
                    </Link>

                    {includeProgressBtn && (
                        <Link
                            to={`/client-progress/${user.id}?year=${year}`}
                            className="flex-grow inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-colors text-center bg-indigo-500 dark:bg-indigo-600
                    text-white
                    hover:bg-indigo-600 dark:hover:bg-indigo-700"
                        >
                            Progress <LineChart className="w-4 h-4"/>
                        </Link>
                    )}


                    <Link
                        to={`/documents/${user.id}?year=${year}`}
                        className="flex-grow inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
                    >
                        Documents <Eye className="w-4 h-4"/>
                    </Link>

                    <button
                        onClick={() => handleArchiveToggle(
                            user.id,
                            user.is_archived,
                            user.profile.client
                        )}
                        className="flex-grow inline-flex items-center justify-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {user.is_archived ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z"/>
                            ) : (
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                             )}
                        </svg>
                        {user.is_archived ? 'Unarchive' : 'Archive'}
                    </button>

                    {includeProfileBtn && (
                        <Link
                            to={`/clients/${user.id}?year=${year}`}
                            className="flex-grow inline-flex items-center justify-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            Profiles <ImProfile className="w-4 h-4"/>
                        </Link>
                    )}
                </div>

                {/* Communication Actions */}
                <div className="flex flex-col
            max-[640px]:flex-row
            min-[1100px]:flex-row
            min-[1315px]:flex-col
            xs:flex-row
            sm:flex-row lg:flex-col gap-2">
                    <Link
                        to={`/view-letters/${user.id}?year=${year}`}
                        className="flex-grow inline-flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors text-center"
                    >
                        View Sent Letters <Mail className="w-4 h-4"/>
                    </Link>

                    <Link
                        to={`/send-letter/${user.id}`}
                        className="flex-grow inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-center"
                    >
                        Send Letter <ArrowRight className="w-4 h-4"/>
                    </Link>
                </div>
            </div>
        </>

    );
}

export default ClientAccountActions;
